import { useMemo, useEffect, type MouseEvent, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useFormik, getIn } from 'formik';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Card, LinearProgress, CardContent, MenuItem, TextField, Grid, Typography } from '@mui/material';

import { state } from '../../../utils/config';
import { Suppliers, PreferredAssessmentDates } from '../steps';
import { Request, Supplier } from '../../../models';
import { usePermissions } from '../../../hooks/usePermissions';
import { useToast } from '../../../hooks/useToast';
import { Status } from '../../../components/request';
import {
	useGetRequestQuery,
	useUpdateRequestMutation,
	useCancelRequestMutation,
} from '../../../slices/apis/requestApi';
import { useGetQuestionnairesQuery } from '../../../slices/apis/questionnaireMgmtApi';
import { BreadCrumbs } from '../../../components/nav-section';
import Tests from './Tests';
import { useRequestRules } from '../../../hooks/useRequestRules';
import { fDateTime } from '../../../utils/formatTime';

export default function EditRequest() {
	const { reqId } = useParams() as { reqId: string };
	const navigate = useNavigate();
	const [
		updateRequest,
		{ isError: isUpdateRequestError, isLoading: isUpdateRequestLoading, isSuccess: isUpdateRequestSuccess },
	] = useUpdateRequestMutation();
	const [
		cancelRequest,
		{ isLoading: isCancelRequestLoading, isSuccess: isCancelRequestSuccess, isError: isCancelRequestError },
	] = useCancelRequestMutation();
	const { showToast, ToastElement } = useToast();

	const { data: request } = useGetRequestQuery(reqId);
	const { allowCancellation } = useRequestRules({ data: request });
	const { can } = usePermissions();
	const canEditQuestionnaire = can('req:qn:manage');
	const canEditSupplier = can('req:update');
	const { data: questionnaires = [] } = useGetQuestionnairesQuery(undefined, { skip: !canEditQuestionnaire });

	const formik = useFormik({
		initialValues: useMemo(() => {
			if (request) {
				return Request.schema.cast(
					{
						...request,
						supplierId: request.supplier.id,
					},
					{
						stripUnknown: true,
					},
				);
			}

			return Request.schema.getDefault();
		}, [request]),
		enableReinitialize: true,
		validationSchema: Request.editSchema,
		onSubmit: async (values) => {
			await updateRequest(values);
		},
	});

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	useEffect(() => {
		if (isUpdateRequestSuccess) {
			showToast('Request has been updated successfully!', 'success');
		}
	}, [isUpdateRequestSuccess, showToast]);

	useEffect(() => {
		if (isUpdateRequestError) {
			showToast('Failed to update your request!', 'error');
		}
	}, [isUpdateRequestError, showToast]);

	useEffect(() => {
		if (isCancelRequestError) {
			showToast('Failed to cancel your request!', 'error');
		}
	}, [isCancelRequestError, showToast]);

	useEffect(() => {
		if (isCancelRequestSuccess) {
			showToast('Request has been cancelled successfully!', 'success');
		}
	}, [isCancelRequestSuccess, showToast]);

	useEffect(() => {
		if (isCancelRequestSuccess) {
			navigate('/dashboard/requests');
		}
	}, [isCancelRequestSuccess, navigate]);

	if (!request) {
		return <LinearProgress />;
	}

	const disableActionButton = () => {
		return isUpdateRequestLoading || isCancelRequestLoading || !Request.isEditable(request.status);
	};

	const onCancelRequest = () => {
		const message = Request.isAssessmentScheduled(request.status)
			? 'Are you sure you want to cancel? A 50% fee applies to appointments cancelled within 24 hours of the scheduled time.'
			: 'Are you sure you want to cancel it?';
		const isConfirmed = window.confirm(message);

		if (isConfirmed) {
			cancelRequest({
				id: request.id,
			});
		}
	};

	const breadCrumbItems = [
		{
			text: 'My Single Requests',
			onClick: handleNavigate,
			dataRoute: '/dashboard/requests',
		},
		{
			text: `Request ${request.id}`,
		},
	];

	const isPreferredAssessmentDateEditable = Request.isPreferredAssessmentTimeEditingAllowed(request.status);

	return (
		<>
			<Helmet>
				<title> Edit Request - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			{ToastElement}

			<Grid container justifyContent="center">
				<Grid size={{ md: 6 }}>
					<Card>
						<CardContent>
							<form onSubmit={formik.handleSubmit}>
								<Grid container spacing={2}>
									<Grid size={{ md: 12 }} container spacing={2} alignItems="center">
										<Grid size={{ md: 12 }}>
											<BreadCrumbs items={breadCrumbItems} />
										</Grid>
										<Grid size={{ md: 6 }}>{formik.values.status && <Status status={formik.values.status} />}</Grid>
									</Grid>
									<Grid size={{ md: 12 }}>
										{isPreferredAssessmentDateEditable ? (
											<PreferredAssessmentDates
												formik={formik}
												prefAssessmentDateLabels={Request.preferredAssessmentDateLabels}
											/>
										) : (
											<>
												<Typography variant="body1" color="text.secondary" gutterBottom>
													Preferred Assessment Dates
												</Typography>
												{request.prefAssessmentDates.map((date) => (
													<Typography key={date?.toString()} variant="body1">
														{fDateTime(date)}
													</Typography>
												))}
											</>
										)}
									</Grid>
									<Grid size={{ md: 6 }}>
										<TextField
											fullWidth
											margin="dense"
											id="firstname"
											name="candidate.firstname"
											label="First Name"
											variant="standard"
											value={formik.values.candidate.firstname}
											onChange={formik.handleChange}
											error={Boolean(
												getIn(formik.touched, 'candidate.firstname') && getIn(formik.errors, 'candidate.firstname'),
											)}
											helperText={
												getIn(formik.touched, 'candidate.firstname') && getIn(formik.errors, 'candidate.firstname')
											}
										/>
									</Grid>
									<Grid size={{ md: 6 }}>
										<TextField
											fullWidth
											margin="dense"
											id="lastname"
											name="candidate.lastname"
											label="Last Name"
											variant="standard"
											value={formik.values.candidate.lastname}
											onChange={formik.handleChange}
											error={Boolean(
												getIn(formik.touched, 'candidate.lastname') && getIn(formik.errors, 'candidate.lastname'),
											)}
											helperText={
												getIn(formik.touched, 'candidate.lastname') && getIn(formik.errors, 'candidate.lastname')
											}
										/>
									</Grid>
									<Grid size={{ md: 6 }}>
										<TextField
											fullWidth
											margin="dense"
											id="phone"
											name="candidate.phone"
											label="Phone"
											variant="standard"
											inputProps={{ inputMode: 'numeric', maxLength: 10 }}
											value={formik.values.candidate.phone}
											onChange={formik.handleChange}
											error={Boolean(
												getIn(formik.touched, 'candidate.phone') && getIn(formik.errors, 'candidate.phone'),
											)}
											helperText={getIn(formik.touched, 'candidate.phone') && getIn(formik.errors, 'candidate.phone')}
										/>
									</Grid>
									<Grid size={{ md: 6 }}>
										<TextField
											fullWidth
											margin="dense"
											id="email"
											name="candidate.email"
											label="Email"
											variant="standard"
											type="email"
											value={formik.values.candidate.email}
											onChange={formik.handleChange}
											error={Boolean(
												getIn(formik.touched, 'candidate.email') && getIn(formik.errors, 'candidate.email'),
											)}
											helperText={getIn(formik.touched, 'candidate.email') && getIn(formik.errors, 'candidate.email')}
										/>
									</Grid>
									<Grid size={{ md: 6 }}>
										<TextField
											fullWidth
											margin="dense"
											id="addrpostcode"
											name="candidate.addrPostcode"
											label="Postcode"
											variant="standard"
											inputProps={{ inputMode: 'numeric', maxLength: 4 }}
											value={formik.values.candidate.addrPostcode}
											onChange={formik.handleChange}
											error={Boolean(
												getIn(formik.touched, 'candidate.addrPostcode') &&
													getIn(formik.errors, 'candidate.addrPostcode'),
											)}
											helperText={
												getIn(formik.touched, 'candidate.addrPostcode') &&
												getIn(formik.errors, 'candidate.addrPostcode')
											}
										/>
									</Grid>
									<Grid size={{ md: 6 }}>
										<TextField
											select
											fullWidth
											margin="dense"
											id="addrstate"
											name="candidate.addrState"
											label="State"
											variant="standard"
											value={formik.values.candidate.addrState}
											onChange={formik.handleChange}
											error={Boolean(
												getIn(formik.touched, 'candidate.addrState') && getIn(formik.errors, 'candidate.addrState'),
											)}
											helperText={
												getIn(formik.touched, 'candidate.addrState') && getIn(formik.errors, 'candidate.addrState')
											}
										>
											{state.map((s) => (
												<MenuItem key={s.name} value={s.label}>
													{s.label}
												</MenuItem>
											))}
										</TextField>
									</Grid>
									<Grid size={{ md: 6 }}>
										<TextField
											fullWidth
											margin="dense"
											id="employer"
											name="candidate.employer"
											label="Employer"
											variant="standard"
											value={formik.values.candidate.employer}
											onChange={formik.handleChange}
											error={Boolean(
												getIn(formik.touched, 'candidate.employer') && getIn(formik.errors, 'candidate.employer'),
											)}
											helperText={
												getIn(formik.touched, 'candidate.employer') && getIn(formik.errors, 'candidate.employer')
											}
										/>
									</Grid>
									<Grid size={{ md: 6 }}>
										<TextField
											fullWidth
											margin="dense"
											id="jobRole"
											name="candidate.jobRole"
											label="Job Role"
											variant="standard"
											value={formik.values.candidate.jobRole ?? ''}
											onChange={formik.handleChange}
											error={Boolean(
												getIn(formik.touched, 'candidate.jobRole') && getIn(formik.errors, 'candidate.jobRole'),
											)}
											helperText={
												getIn(formik.touched, 'candidate.jobRole') && getIn(formik.errors, 'candidate.jobRole')
											}
										/>
									</Grid>
									{canEditQuestionnaire && (
										<Grid size={{ md: 6 }}>
											<TextField
												select
												fullWidth
												margin="dense"
												id="questionnaireId"
												name="candidate.questionnaireId"
												label="Questionnaire"
												variant="standard"
												value={formik.values.candidate.questionnaireId ?? ''}
												onChange={formik.handleChange}
											>
												<MenuItem value="">None</MenuItem>
												{questionnaires.map((q) => (
													<MenuItem key={q.id} value={q.id}>
														{q.name}
													</MenuItem>
												))}
											</TextField>
										</Grid>
									)}
									<Grid size={{ md: 12 }}>
										<Tests formik={formik} />
									</Grid>
									<Grid size={{ md: 12 }}>
										<Suppliers
											formik={formik}
											requestEdit
											editable={canEditSupplier || Supplier.isEditable(request.status)}
										/>
									</Grid>
									<Grid size={{ md: 6 }}>
										<Grid container justifyContent="start" spacing={4}>
											<Grid>
												<Button
													loading={isUpdateRequestLoading}
													variant="outlined"
													color="primary"
													type="submit"
													disabled={disableActionButton()}
												>
													<span>Update</span>
												</Button>
											</Grid>
											<Grid>
												<Button
													color="error"
													variant="outlined"
													data-route="/dashboard/requests"
													onClick={handleNavigate}
												>
													Back
												</Button>
											</Grid>
										</Grid>
									</Grid>
									{allowCancellation && (
										<Grid size={{ md: 6 }}>
											<Grid container justifyContent="flex-end">
												<Grid>
													<Button
														loading={isCancelRequestLoading}
														variant="outlined"
														color="error"
														disabled={disableActionButton()}
														onClick={onCancelRequest}
													>
														<span>Cancel Request</span>
													</Button>
												</Grid>
											</Grid>
										</Grid>
									)}
								</Grid>
							</form>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</>
	);
}
